import React, { useState, useRef, useEffect } from "react";
import { Search, Bell, Plus, ChevronDown, LogOut, UserCircle, Menu, Loader2, Users, Briefcase, FileText, Receipt, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import NewJobModal from "./NewJobModal";
import { TimeClockPanel, useTimeClock } from "./TimeClock";
import { searchApi } from "../lib/api";
import CreditsPill from "./CreditsPill";

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchApi.global(term);
        setRes(data); setOpen(true);
      } catch { setRes(null); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const groups = res ? [
    { key: "customers", label: "Customers", icon: Users, items: res.customers, to: (id) => `/customers?id=${encodeURIComponent(id)}` },
    { key: "jobs", label: "Jobs", icon: Briefcase, items: res.jobs, to: (id) => `/jobs?id=${encodeURIComponent(id)}` },
    { key: "estimates", label: "Estimates", icon: FileText, items: res.estimates, to: (id) => `/estimates?id=${encodeURIComponent(id)}` },
    { key: "invoices", label: "Invoices", icon: Receipt, items: res.invoices, to: (id) => `/estimates?id=${encodeURIComponent(id)}` },
  ].filter((g) => g.items && g.items.length) : [];
  const total = groups.reduce((n, g) => n + g.items.length, 0);

  const go = (to) => { setOpen(false); setQ(""); setRes(null); navigate(to); };

  return (
    <div className="relative hidden md:block" ref={boxRef}>
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (res) setOpen(true); }}
        placeholder="Search customers, jobs, invoices..."
        data-testid="global-search-input"
        className="w-80 h-9 pl-9 pr-8 rounded-md border border-slate-200 bg-slate-50 text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white transition-colors"
      />
      {q && (
        <button onClick={() => { setQ(""); setRes(null); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-slate-200 flex items-center justify-center" aria-label="Clear search">
          <X className="h-3.5 w-3.5 text-slate-400" />
        </button>
      )}
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" className="absolute right-0 mt-1.5 w-[420px] max-h-[70vh] overflow-y-auto bg-white rounded-lg border border-slate-200 shadow-xl z-50">
          {loading && !res ? (
            <div className="px-4 py-6 flex items-center justify-center text-slate-400 text-[13px]"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Searching…</div>
          ) : total === 0 ? (
            <div className="px-4 py-6 text-center text-[13px] text-slate-400" data-testid="global-search-empty">No results for “{q.trim()}”</div>
          ) : (
            groups.map((g) => (
              <div key={g.key} className="py-1 border-b border-slate-50 last:border-0">
                <div className="px-3 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><g.icon className="h-3.5 w-3.5" /> {g.label}</div>
                {g.items.map((it) => (
                  <button key={it.id} onClick={() => go(g.to(it.id))} data-testid={`search-result-${it.type}-${it.id}`}
                    className="w-full px-3 py-2 text-left hover:bg-blue-50/60 flex items-center gap-3 transition-colors">
                    <div className="h-7 w-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><g.icon className="h-3.5 w-3.5" /></div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-slate-800 truncate">{it.label}</div>
                      {it.sub && <div className="text-[11.5px] text-slate-500 truncate">{it.sub}</div>}
                    </div>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function initials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function roleLabel(role) {
  if (!role) return "Member";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export default function Topbar({ title, subtitle, actions }) {
  const { user, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const menuRef = useRef(null);
  const loggedIn = user && user !== false;
  const canTimeclock = !!loggedIn && hasModule("timeclock");
  const clock = useTimeClock(canTimeclock);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const displayName = user && user !== false ? user.name || user.email : "—";
  const displayRole = user && user !== false ? roleLabel(user.role) : "";
  const avatar = user && user !== false ? initials(user.name, user.email) : "--";
  const canCreateJob = user && user !== false && user.role !== "technician";

  return (
    <header className="h-16 bg-white border-b border-slate-200 px-4 md:px-6 hidden md:flex items-center justify-between sticky top-0 z-30">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        <button
          onClick={() => window.dispatchEvent(new Event("toggle-mobile-nav"))}
          data-testid="mobile-nav-toggle"
          aria-label="Open menu"
          className="md:hidden h-9 w-9 -ml-1 shrink-0 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-700"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-[17px] font-semibold text-slate-900 leading-tight truncate">{title}</h1>
          {subtitle && <p className="text-[12px] text-slate-500 leading-tight truncate">{subtitle}</p>}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <GlobalSearch />

        {actions}

        <CreditsPill />

        {canCreateJob && (
          <Button
            onClick={() => setNewJobOpen(true)}
            data-testid="topbar-new-job-button"
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-medium gap-1.5 px-3"
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            New Job
          </Button>
        )}

        <button className="relative h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center transition-colors">
          <Bell className="h-[18px] w-[18px] text-slate-600" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="topbar-user-menu-button"
            className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-md hover:bg-slate-100 transition-colors"
          >
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white text-[11px] font-semibold flex items-center justify-center">
              {avatar}
            </div>
            <div className="text-left hidden lg:block">
              <div data-testid="topbar-user-name" className="text-[12px] font-semibold text-slate-800 leading-tight truncate max-w-[140px]">
                {displayName}
              </div>
              <div data-testid="topbar-user-role" className="text-[10.5px] text-slate-500 leading-tight">
                {displayRole}
              </div>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>

          {menuOpen && (
            <div
              data-testid="topbar-user-menu"
              className="absolute right-0 mt-1.5 w-56 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden z-40"
            >
              <div className="px-3 py-2.5 border-b border-slate-100">
                <div className="text-[13px] font-semibold text-slate-800 truncate">{displayName}</div>
                {user && user !== false && (
                  <div className="text-[11.5px] text-slate-500 truncate">{user.email}</div>
                )}
              </div>
              {canTimeclock && <TimeClockPanel clock={clock} />}
              <button
                disabled
                className="w-full px-3 py-2 text-left text-[13px] text-slate-400 cursor-not-allowed flex items-center gap-2"
              >
                <UserCircle className="h-4 w-4" /> Profile (soon)
              </button>
              <button
                onClick={onLogout}
                data-testid="topbar-logout-button"
                className="w-full px-3 py-2 text-left text-[13px] text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      <NewJobModal
        open={newJobOpen}
        onClose={() => setNewJobOpen(false)}
        onCreated={(job) => {
          window.dispatchEvent(new CustomEvent("job-created", { detail: job }));
        }}
      />
    </header>
  );
}
